import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureSurface } from '../FeatureSurfaceContext';
import { FEATURE_SURFACE_CONFIG } from '../featureSurfaceConfig';
import { useDefaultAutomationMetadata } from '../useAutomationTemplates';
import { CreateTemplateDialog } from './create-template-dialog';
import { DefaultTemplateButtons } from './DefaultTemplateButtons';
import { ReputationDefaultsMenu, ReputationGenerationBanner } from './reputation-defaults-menu';
import styles from './AutomationsTab.module.css';
import { TemplatesManager } from './templates-manager';
import { TemplatesTableControls } from './TemplatesTableControls';

const HEADING_BY_FEATURE = {
  engage: { title: 'Automation Templates', description: 'Manage your automated campaigns' },
  reputation: {
    title: 'Review Request Automations',
    description: 'Manage the review and feedback requests your tenants send',
  },
} as const;

export const AutomationsTab: React.FC = () => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const navigate = useNavigate();
  const featureSurface = useFeatureSurface();
  const config = FEATURE_SURFACE_CONFIG[featureSurface.feature];
  const heading = HEADING_BY_FEATURE[featureSurface.feature];

  const { data: defaultMetadata, isLoading: isLoadingMetadata } = useDefaultAutomationMetadata();

  const handleCreateTemplate = () => {
    setCreateDialogOpen(true);
  };

  const handleTemplateCreated = (templateId: string) => {
    navigate(featureSurface.buildAutomationEditPath(templateId));
  };

  const showsEngageDefaults =
    config.showsEngageDefaultTemplates && !isLoadingMetadata && !!defaultMetadata?.metadata;

  return (
    <div className="space-y-6">
      <section className={styles.tabBodyHeader}>
        <div className={styles.headerText}>
          <h2>{heading.title}</h2>
          <p>{heading.description}</p>
        </div>
        {(config.showsReputationDefaultsMenu || config.canCreateBlankTemplate) && (
          <div className={styles.headerButtons}>
            {config.showsReputationDefaultsMenu && <ReputationDefaultsMenu />}
            {config.canCreateBlankTemplate && (
              <TemplatesTableControls onCreateTemplate={handleCreateTemplate} />
            )}
          </div>
        )}
      </section>

      {config.showsReputationDefaultsMenu && <ReputationGenerationBanner />}
      {showsEngageDefaults && <DefaultTemplateButtons metadata={defaultMetadata?.metadata ?? []} />}
      {config.canCreateBlankTemplate && (
        <CreateTemplateDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onTemplateCreated={handleTemplateCreated}
          defaultTemplates={defaultMetadata?.metadata || []}
        />
      )}

      <TemplatesManager />
    </div>
  );
};
